// ============================================
// BulkAdjustModal
// Điều chỉnh tồn kho HÀNG LOẠT (sheet 2 — mục #23, phần bulk)
// ============================================
//
// Khác AdjustStockModal ở chỗ đi qua POST /inventory/bulk-adjust/ với tối đa
// 100 dòng một lô. Ba đặc tính của endpoint quyết định toàn bộ thiết kế màn này:
//
// 1. `idempotency_key` nằm TRONG TỪNG DÒNG. Ta sinh key một lần cho mỗi dòng
//    ngay khi mở modal và GIỮ NGUYÊN qua các lần bấm Gửi lại — đó chính là
//    điều làm cho việc thử lại sau lỗi mạng an toàn. Sinh key mới mỗi lần gửi
//    thì cơ chế chống trùng vô nghĩa và kho bị trừ hai lần.
// 2. Response LUÔN 200. Không có `onError` nào bắt được dòng hỏng — phải đọc
//    `results[]` và hiện lỗi ngay tại dòng đó.
// 3. `quantity` là số nguyên KHÔNG ÂM. Hàng cân ký chưa đi được đường này.
//
// Người dùng chọn dòng ở bảng tồn kho rồi mở modal, nên `product_id` có sẵn —
// không phải dò SKU, không có dòng "không tìm thấy sản phẩm".

import { useEffect, useMemo, useState } from 'react';
import type { BulkAdjustItem, LossReason, StockAdjustmentType, UUID } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import { useBulkAdjust, BULK_ADJUST_MAX_ITEMS } from '../../../hooks';
import type { InventoryItem } from './InventoryTableRow';

/**
 * Nguyên nhân hao hụt (mục #220). CHUNG cho cả lô, không phải per-row: `type`
 * đã chung cho cả lô rồi (một ô chọn ở trên bảng, không phải mỗi dòng một
 * loại), nên nguyên nhân cũng theo đúng nguyên tắc đó — một lượt "trừ hàng
 * hết hạn" thường trả lời được bằng MỘT nguyên nhân cho cả rổ hàng đang chọn.
 */
const LOSS_REASONS: { value: LossReason; labelKey: string }[] = [
  { value: 'THEFT', labelKey: 'lossReasonTheft' },
  { value: 'EXPIRED', labelKey: 'lossReasonExpired' },
  { value: 'DAMAGED', labelKey: 'lossReasonDamaged' },
  { value: 'OTHER', labelKey: 'lossReasonOther' },
];

/** Cùng tập loại cho phép chỉnh tay như AdjustStockModal — xem giải thích ở đó. */
const MANUAL_TYPES: {
  value: Extract<StockAdjustmentType, 'SET_ABSOLUTE' | 'INCREASE' | 'DECREASE' | 'DAMAGE'>;
  labelKey: string;
  direction: 'absolute' | 'out' | 'in';
}[] = [
  { value: 'SET_ABSOLUTE', labelKey: 'adjustTypeSetAbsolute', direction: 'absolute' },
  { value: 'INCREASE', labelKey: 'adjustTypeIncrease', direction: 'in' },
  { value: 'DECREASE', labelKey: 'adjustTypeDecrease', direction: 'out' },
  { value: 'DAMAGE', labelKey: 'adjustTypeDamage', direction: 'out' },
];

/** Key ổn định cho một dòng trong suốt vòng đời modal. */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Môi trường không có Web Crypto (test node cũ): vẫn phải là chuỗi KHÁC RỖNG.
  return `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

interface Row {
  item: InventoryItem;
  quantity: string;
  idempotencyKey: string;
}

interface BulkAdjustModalProps {
  items: InventoryItem[];
  storeId: UUID | undefined;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BulkAdjustModal({ items, storeId, onClose, onSuccess }: BulkAdjustModalProps) {
  const { t } = useI18n();
  const { mutate: bulkAdjust, isPending } = useBulkAdjust();

  const [type, setType] = useState<(typeof MANUAL_TYPES)[number]['value']>('SET_ABSOLUTE');
  const [reason, setReason] = useState('');
  const [lossReason, setLossReason] = useState<LossReason | ''>('');
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  /** BE bắt buộc `loss_reason` cho hai loại này, 400 nếu gửi kèm loại khác. */
  const requiresLossReason = type === 'DECREASE' || type === 'DAMAGE';
  /**
   * Kết quả từng dòng sau lần gửi gần nhất, tra theo `idempotency_key`.
   *
   * BE trả hai mảng `successful` / `failed` chứ KHÔNG gắn nhãn trạng thái lên
   * từng dòng, và không phân biệt "ghi mới" với "đã ghi từ lần trước" — dòng
   * gửi trùng nằm luôn trong `successful` với cùng `adjustment_id`. Nên ở đây
   * chỉ có hai trạng thái.
   */
  const [results, setResults] = useState<Record<string, { ok: boolean; error?: string }>>({});
  /** Dòng hỏng mà BE trả về không kèm `idempotency_key` — không quy được về hàng nào. */
  const [unmatchedFailedCount, setUnmatchedFailedCount] = useState(0);

  // Sinh key MỘT LẦN cho mỗi dòng khi tập dòng đổi. Không đưa vào state khởi
  // tạo trong lúc render để tránh sinh lại mỗi lần re-render.
  useEffect(() => {
    setRows(items.map((item) => ({ item, quantity: '', idempotencyKey: newIdempotencyKey() })));
    setReason('');
    setType('SET_ABSOLUTE');
    setLossReason('');
    setError(null);
    setResults({});
    setUnmatchedFailedCount(0);
  }, [items]);

  const activeType = MANUAL_TYPES.find((x) => x.value === type) ?? MANUAL_TYPES[0];

  // Đổi sang loại không cần nguyên nhân thì xoá lựa chọn — giữ lại là gửi kèm
  // `loss_reason` cho INCREASE/SET_ABSOLUTE và ăn 400 nguyên cả lô.
  useEffect(() => {
    if (!requiresLossReason) setLossReason('');
  }, [requiresLossReason]);

  const parsedRows = useMemo(
    () =>
      rows.map((row) => {
        const parsed = Number.parseInt(row.quantity, 10);
        // Không âm; `SET_ABSOLUTE` cho phép 0 (đếm ra hết hàng là hợp lệ),
        // các loại khác thì 0 vô nghĩa nên coi như chưa nhập.
        const valid =
          Number.isFinite(parsed) &&
          parsed >= 0 &&
          (activeType.direction === 'absolute' || parsed !== 0);
        const value = valid ? Math.abs(parsed) : null;

        // SET_ABSOLUTE: tồn sau CHÍNH LÀ số đếm, không cộng vào tồn cũ.
        const projected =
          value === null
            ? null
            : activeType.direction === 'absolute'
              ? value
              : activeType.direction === 'out'
                ? row.item.currentQuantity - value
                : row.item.currentQuantity + value;

        return { ...row, value, projected };
      }),
    [rows, activeType.direction]
  );

  const filledRows = parsedRows.filter((r) => r.value !== null);
  const negativeRows = parsedRows.filter((r) => r.projected !== null && r.projected < 0);

  const setQuantity = (idempotencyKey: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.idempotencyKey === idempotencyKey ? { ...row, quantity: value } : row))
    );
  };

  const handleSubmit = () => {
    setError(null);

    if (!storeId) {
      setError(t('adjustErrorNoStore'));
      return;
    }
    if (filledRows.length === 0) {
      setError(t('bulkAdjustErrorNoQuantity'));
      return;
    }
    if (filledRows.length > BULK_ADJUST_MAX_ITEMS) {
      setError(t('bulkAdjustErrorTooMany').replace('{max}', String(BULK_ADJUST_MAX_ITEMS)));
      return;
    }
    if (!reason.trim()) {
      setError(t('adjustErrorReason'));
      return;
    }
    if (requiresLossReason && !lossReason) {
      setError(t('adjustErrorLossReason'));
      return;
    }
    if (negativeRows.length > 0) {
      setError(t('adjustErrorNegative'));
      return;
    }

    // Tên field ở đây KHÁC endpoint một dòng: `type` + `quantity`, không phải
    // `adjustment_type` + `quantity_change`. Xem ghi chú ở `BulkAdjustItem`.
    const payload: BulkAdjustItem[] = filledRows.map((row) => ({
      product_id: row.item.productId,
      store_id: storeId,
      type,
      // KHÔNG ÂM — chiều do `type` quyết định ở BE.
      quantity: row.value as number,
      reason: reason.trim(),
      // Chỉ đính kèm khi BẮT BUỘC — type khác mà có field này là 400 CẢ LÔ.
      ...(requiresLossReason && lossReason ? { loss_reason: lossReason } : {}),
      idempotency_key: row.idempotencyKey,
    }));

    bulkAdjust(payload, {
      onSuccess: (res) => {
        const byKey: Record<string, { ok: boolean; error?: string }> = {};
        for (const r of res.successful ?? []) {
          if (r.idempotency_key) byKey[r.idempotency_key] = { ok: true };
        }
        // `error` là tên field lỗi DUY NHẤT (BE xác nhận không có `message`).
        // `idempotency_key` có thể null nếu dòng đó không gửi key — không xảy
        // ra ở đây vì FE luôn sinh key cho mọi dòng, nhưng nếu có thì dòng đó
        // không khớp về hàng nào được và rơi vào cảnh báo tổng bên dưới.
        let unmatchedFailures = 0;
        for (const r of res.failed ?? []) {
          if (r.idempotency_key) {
            byKey[r.idempotency_key] = { ok: false, error: r.error ?? undefined };
          } else {
            unmatchedFailures += 1;
          }
        }
        setResults(byKey);
        setUnmatchedFailedCount(unmatchedFailures);

        // CHỈ đóng khi không còn dòng hỏng. Còn dòng lỗi thì giữ modal để
        // người dùng thấy dòng nào hỏng và sửa — gửi lại an toàn vì key giữ
        // nguyên: dòng đã ăn quay về trong `successful` với cùng adjustment_id
        // và kho không bị trừ lần nữa.
        const failed = res.summary?.failed ?? res.failed?.length ?? 0;
        if (failed === 0) {
          onSuccess?.();
          onClose();
        }
      },
      onError: (err: unknown) => {
        // Nhánh này chỉ còn lỗi tầng vận chuyển (mạng, 500, 401) — lỗi nghiệp
        // vụ từng dòng không bao giờ rơi vào đây vì endpoint trả 200.
        setError(err instanceof Error ? err.message : t('adjustErrorGeneric'));
      },
    });
  };

  if (items.length === 0) return null;

  const failedCount = Object.values(results).filter((r) => !r.ok).length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('bulkAdjustTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {t('bulkAdjustSelectedCount').replace('{count}', String(items.length))}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          {/* Loại + lý do dùng CHUNG cho cả lô */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('adjustTypeLabel')}
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {MANUAL_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setType(option.value)}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                    type === option.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {requiresLossReason && (
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('lossReasonLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {LOSS_REASONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setLossReason(option.value)}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                      lossReason === option.value
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">{t('bulkAdjustReasonSharedHint')}</p>
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={t('adjustReasonHint')}
              className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
            <p className="mt-1 text-xs text-slate-500">{t('bulkAdjustReasonSharedHint')}</p>
          </div>

          {/* Bảng số lượng từng dòng */}
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t('product')}</th>
                  <th className="w-24 px-3 py-2 text-right">{t('currentStock')}</th>
                  <th className="w-28 px-3 py-2 text-right">
                    {activeType.direction === 'absolute' ? t('adjustCountedQuantity') : t('quantity')}
                  </th>
                  <th className="w-28 px-3 py-2 text-right">{t('bulkAdjustAfter')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {parsedRows.map((row) => {
                  const result = results[row.idempotencyKey];
                  return (
                    <tr key={row.idempotencyKey} className={result && !result.ok ? 'bg-red-50' : undefined}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900">{row.item.productName}</div>
                        <div className="text-xs text-slate-500">{row.item.sku}</div>
                        {result && !result.ok && (
                          <div className="mt-0.5 text-xs font-semibold text-red-600">
                            {result.error || t('adjustErrorGeneric')}
                          </div>
                        )}
                        {result?.ok && (
                          <div className="mt-0.5 text-xs font-semibold text-emerald-600">
                            {t('bulkAdjustRowApplied')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600">{row.item.currentQuantity}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          value={row.quantity}
                          onChange={(e) => setQuantity(row.idempotencyKey, e.target.value)}
                          className="w-full rounded-md border border-slate-200 px-2 py-1 text-right text-sm outline-none focus:border-primary-400"
                        />
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-bold ${
                          row.projected === null
                            ? 'text-slate-300'
                            : row.projected < 0
                              ? 'text-red-600'
                              : 'text-slate-900'
                        }`}
                      >
                        {row.projected ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {failedCount > 0 && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {t('bulkAdjustPartialFailure').replace('{count}', String(failedCount))}
            </div>
          )}

          {/* Dòng hỏng không kèm key thì không tô đỏ được ở bảng trên — phải
              nói ra, im lặng ở đây là báo sai rằng mọi dòng đều vào. */}
          {unmatchedFailedCount > 0 && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {t('bulkAdjustUnmatchedFailure').replace('{count}', String(unmatchedFailedCount))}
            </div>
          )}

          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-6 py-4">
          <span className="text-xs text-slate-500">
            {t('bulkAdjustFilledCount')
              .replace('{filled}', String(filledRows.length))
              .replace('{total}', String(rows.length))}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isPending}
              className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {isPending ? t('saving') : t('adjustSubmit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
